import React, { createContext, useContext, useEffect, useState } from "react";
import {
  TenantConfig,
  getTenantConfig,
  applyTenantConfig,
  getTenantId,
} from "../utils/tenant";
import { updateConfigIdHeader } from "../api/client";
import apiClient from "../api/client";
import { sleep } from "../utils";

interface TenantContextType {
  config: TenantConfig | null;
  loading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextType>({
  config: null,
  loading: true,
  error: null,
});

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

interface TenantProviderProps {
  children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTenantConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        const tenantId = getTenantId();

        // Check if we have a valid tenant ID
        if (!tenantId || tenantId === "default") {
          await sleep(1000);
          setLoading(false);
          return;
        }

        const tenantConfig = await getTenantConfig();

        if (tenantConfig) {
          setConfig(tenantConfig);
          applyTenantConfig(tenantConfig);

          // Fetch and inject config ID for API headers
          try {
            const configResponse = await apiClient.config.getConfigBySlug(
              tenantId
            );
            const configId = configResponse.data._id;
            updateConfigIdHeader(configId);
            console.log(
              `Config ID ${configId} injected for tenant ${tenantId}`
            );
          } catch (configError) {
            console.error("Failed to fetch config ID:", configError);
            // Don't fail the entire tenant loading if config ID fetch fails
          }
        } else {
          setError("Tenant configuration not found");
        }
      } catch (err) {
        setError("Failed to load tenant configuration");
        console.error("Error loading tenant config:", err);
      } finally {
        await sleep(1000);
        setLoading(false);
      }
    };

    loadTenantConfig();
  }, []);

  return (
    <TenantContext.Provider value={{ config, loading, error }}>
      {children}
    </TenantContext.Provider>
  );
};
